import { useLocation, useMatches, useRouteLoaderData } from 'react-router'
import { buildBreadcrumbLd } from '#app/seo/structured-data.ts'

/**
 * Get the origin URL from the root loader
 */
export function useOrigin() {
	const root = useRouteLoaderData('root') as
		| { requestInfo?: { origin?: string } }
		| undefined
	return root?.requestInfo?.origin || 'https://rentdfw.com'
}

/**
 * Get the canonical URL for the current route or a specific path
 */
export function useCanonical(relativePath?: string) {
	const origin = useOrigin()
	const location = useLocation()
	const path = relativePath ?? location.pathname
	return origin + (path.startsWith('/') ? path : `/${path}`)
}

type CrumbItem = { name: string; url: string }

/**
 * Type for route handle breadcrumb function
 */
export type BreadcrumbHandle = (args: {
	data: unknown
	params: Record<string, string>
	pathname: string
}) => CrumbItem | null | undefined

/**
 * Automatically generate breadcrumb JSON-LD from route handles
 * Each route can export a handle.breadcrumb function that returns { name, url }
 */
export function useBreadcrumbLd() {
	const origin = useOrigin()
	const matches = useMatches()
	const items: CrumbItem[] = []

	for (const match of matches) {
		const handle = match.handle as { breadcrumb?: BreadcrumbHandle } | undefined
		if (typeof handle?.breadcrumb === 'function') {
			const crumb = handle.breadcrumb({
				data: match.data,
				params: match.params as Record<string, string>,
				pathname: match.pathname,
			})
			if (crumb?.name && crumb?.url) {
				const absoluteUrl = crumb.url.startsWith('http')
					? crumb.url
					: `${origin}${crumb.url.startsWith('/') ? crumb.url : `/${crumb.url}`}`
				items.push({ name: crumb.name, url: absoluteUrl })
			}
		}
	}

	// Only return breadcrumb JSON-LD if we have at least 2 items (home + current)
	return items.length >= 2 ? buildBreadcrumbLd(items) : null
}
